// Core
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';

// Interfaces
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { DirectiveModule } from 'v2/modules/shared/directives/directive.module';
import { trackById } from 'v2/modules/shared/util/track-by.util';
import { UiModule } from 'v2/modules/ui/ui.module';

import { PaginationConfig } from '../../../shared/configs';
import { IPagination } from '../../../shared/interfaces';
import {
  AddressTransformationPipe,
  AddressTransformationType,
} from '../../../shared/pipes/address-transformation.pipe';
import { IClientShipping } from '../../interfaces';
import { ClientShippingAddressesFormatterPipe } from '../../pipes/client-shipping-addresses-formatter.pipe';

import { IKeyValue } from './../../../shared/interfaces/common.interface';

// Modules

// Pipes

// Services
import { ClientShippingApiService } from './../../../shared/services/api/client/client-shipping-api.service';

// Utility

// RxJS

import { formatInlineAddress } from './../../../shared/util/address-transformation.utils';

@Component({
  selector: 'lum-client-shipping-address-accessor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    DirectiveModule,
    ClientShippingAddressesFormatterPipe,
    UiModule,
    AddressTransformationPipe,
  ],
  templateUrl: './client-shipping-address-accessor.component.html',
  styleUrls: ['./client-shipping-address-accessor.component.scss'],
  providers: [
    AddressTransformationPipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ClientShippingAddressAccessorComponent),
      multi: true,
    },
  ],
})
export class ClientShippingAddressAccessorComponent
  implements ControlValueAccessor, OnDestroy, OnChanges
{
  @Input()
  businessAccountId :number = 0;

  @Input()
  isUpdateMode :boolean = false;

  @Input()
  overrideShippingAddress :Partial<IClientShipping> | null = null;

  @Output()
  selectedAddressEvent = new EventEmitter<IClientShipping>();

  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: 1,
    last_page: 0,
    path: '',
    per_page: PaginationConfig.perPage,
    to: 0,
    total: 0,
  };
  hasLoadedUpdateMode :boolean = false;
  companyName :string = '';

  addresses :IClientShipping[] = [];
  selectedItem :IClientShipping;
  selectedItemId :number = null;
  countries :IKeyValue[] = [];
  loadBusinessAcountShippingAddress$ = new BehaviorSubject<boolean>(false);
  loadingShippingAddresses$ = new BehaviorSubject<boolean>(false);

  trackById = trackById;

  private onDestroy$ = new Subject();
  addressTransformationType = AddressTransformationType;

  constructor(
    private clientShippingApiService :ClientShippingApiService,
    private changeDetectorRef :ChangeDetectorRef,
    private addressTransformationPipe :AddressTransformationPipe,
  ) {}

  ngOnChanges({ businessAccountId, overrideShippingAddress } :SimpleChanges) :void {
    if (
      businessAccountId?.currentValue &&
      businessAccountId?.currentValue !== businessAccountId?.previousValue
    ) {
      if (this.isUpdateMode && !this.hasLoadedUpdateMode) {
        if (this.selectedItemId !== null) {
          this.loadShippingAddressById(this.selectedItemId);
          this.hasLoadedUpdateMode = true;
          this.loadAddresses();
        }
      } else {
        this.resetValues();
        this.loadDefaultShippingAddressByBusinessId();
        this.loadAddresses();
      }
    }

    if(overrideShippingAddress?.currentValue &&
      overrideShippingAddress?.currentValue !== overrideShippingAddress?.previousValue
    ) {
      this.checkAndApplyOverride(overrideShippingAddress?.currentValue);
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  loadAddresses(params ?:{ companyName ?:string; page ?:string }) :void {
    const { companyName = '', page = '1' } = params || {};
    if (this.businessAccountId) {
      this.loadingShippingAddresses$.next(true);
      this.clientShippingApiService
        .getV2({
          businessAccountId: this.businessAccountId,
          params: { companyName, page },
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response) => {
            if (response?.data) {
              const responseData = response.data;
              this.addresses =
                this.pagination.current_page === 1
                  ? responseData
                  : [...(this.addresses ?? []), ...responseData];

              this.pagination = response.meta ?? this.pagination;
            }
            this.loadingShippingAddresses$.next(false);
            this.changeDetectorRef.detectChanges();
          },
          error: (e) => {
            this.loadingShippingAddresses$.next(false);
          },
        });
    }
  }

  onSelectItem(
    selectedItem :IClientShipping,
    event :MatOptionSelectionChange | null = null,
  ) :void {
    if (!event?.isUserInput) {
      return;
    }

    const selectedItemId = selectedItem?.id ?? null;
    if (selectedItemId === this.selectedItemId) {
      return;
    }

    this.selectedItemId = selectedItemId; // Update local value
    this.selectedItem = selectedItem; // Update local value
    this.selectedAddressEvent.emit(selectedItem); // Emit selected address
    this.onChange(selectedItemId); // Notify parent form of change
    this.onTouched(); // Notify that the control was touched
  }

  onChange = (value :number) => {};
  onTouched = () => {};

  writeValue(value :number) :void {
    if (this.selectedItemId === value) {
      return;
    }
    this.selectedItemId = value;
  }

  registerOnChange(fn :any) :void {
    this.onChange = fn;
  }

  registerOnTouched(fn :any) :void {
    this.onTouched = fn;
  }

  displayFn(value :IClientShipping) :string {
    if (!value) {
      return '';
    }

    return `${value?.companyName ? value.companyName + ' - ' : ''} ${formatInlineAddress.fromClientShipping(value)}`;
  }

  private checkAndApplyOverride(override :Partial<IClientShipping>) {
    if (!override) return;
  
    const matches = this.addresses.find((addr) =>
      addr.address === override.address &&
      addr.addressLine2 === override.addressLine2 &&
      addr.city === override.city &&
      addr.state === override.state &&
      addr.postalCode === override.postalCode &&
      addr.country === override.country,
    );
  
    if (!matches) {
      const synthetic :any = {
        id: Math.floor(Math.random() * 1_000_000), // pseudo-random
        address: override.address || '',
        addressLine2: override.addressLine2 || '',
        city: override.city || '',
        state: override.state || '',
        postalCode: override.postalCode || '',
        country: override.country || '',
        // Fill in other necessary fields with fallbacks
      };
  
      this.addresses = [synthetic, ...this.addresses];
      this.selectedItem = synthetic;
      this.selectedItemId = synthetic.id;
      this.onSelectItem(synthetic);
    }
  }
  

  private loadShippingAddressById(addressId :number) :void {
    if (this.businessAccountId && addressId) {
      this.loadBusinessAcountShippingAddress$.next(true);
      this.clientShippingApiService
        .getById({
          businessAccountId: this.businessAccountId,
          addressId,
          cache: true,
        })
        .pipe(
          finalize(() => this.loadBusinessAcountShippingAddress$.next(false)),
          takeUntil(this.onDestroy$),
        )
        .subscribe({
          next: (selectedAddress :IClientShipping) => {
            this.addresses = [selectedAddress];
            this.selectedItem = selectedAddress;
            this.onSelectItem(selectedAddress);
          },
        });
    }
  }

  private loadDefaultShippingAddressByBusinessId() :void {
    if (this.businessAccountId) {
      this.loadBusinessAcountShippingAddress$.next(true);
      this.clientShippingApiService
        .getDefault({ businessAccountId: this.businessAccountId })
        .pipe(
          finalize(() => this.loadBusinessAcountShippingAddress$.next(false)),
          takeUntil(this.onDestroy$),
        )
        .subscribe({
          next: (selectedAddress :IClientShipping) => {
            this.addresses = [selectedAddress];
            this.selectedItem = selectedAddress;
            this.onSelectItem(selectedAddress);
          },
        });
    }
  }

  resetValues() {
    this.onChange(null);
    this.selectedItem = null;
    this.selectedItemId = null;
    this.companyName = '';
    this.addresses = [];
    this.pagination = {
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: PaginationConfig.perPage,
      to: 0,
      total: 0,
    };
  }

  onChangeEvent(keyword :string) {
    this.companyName = keyword;
    this.addresses = [];
    this.pagination = {
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: PaginationConfig.perPage,
      to: 0,
      total: 0,
    };
    this.loadAddresses({
      companyName: this.companyName,
    });
  }

  onInitialLoadAddresses() :void {
    if (this.pagination.current_page === 1) {
      this.loadAddresses();
    }
  }

  onLazyLoadAddresses() :void {
    if (this.pagination.last_page > this.pagination.current_page) {
      this.pagination.current_page += 1;

      this.loadAddresses({
        companyName: this.companyName,
        page: this.pagination.current_page.toString(),
      });
    }
  }
}
